// Email verification functions
// Handles email verification for new users

import { sendEmailVerification } from 'firebase/auth';
import { getAuth } from '../firebase/config';

interface EmailVerificationResult {
  success: boolean;
  error?: string;
}

/**
 * Sends a verification email to the current user
 */
export async function sendVerificationEmail(): Promise<EmailVerificationResult> {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      return {
        success: false,
        error: 'No user is currently signed in',
      };
    }
    
    if (user.emailVerified) {
      return {
        success: false,
        error: 'Email is already verified',
      };
    }
    
    await sendEmailVerification(user);
    
    // Log verification email sent
    // Note: We might not have organizationId here, but that's okay
    // We'll skip audit logging for email verification since it's not a standard auth action
    // and we don't have organizationId at this point
    
    return {
      success: true,
    };
  } catch (error) {
    console.error('Email verification error:', error);
    return {
      success: false,
      error: (error as Error).message,
    };
  }
}

/**
 * Checks if the current user's email is verified
 */
export function isEmailVerified(): boolean {
  const auth = getAuth();
  const user = auth.currentUser;
  return user?.emailVerified ?? false;
}

/**
 * Reloads the current user to check for email verification status
 */
export async function reloadUser(): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (user) {
    await user.reload();
  }
}

